import { Box, Typography } from '@mui/material';
import kebabcase from 'lodash/kebabCase';
import { useParams } from 'react-router-dom';

import { YearbookParams } from '../..';
import { ShareButton } from '../../components/ShareButton';
import { colors, sectionTitleGradient } from '../../constants';
import { SponsorLevel, sponsors } from '../../data/sponsors';

interface SponsorsProps {
  id: string;
}

interface TierConfig {
  level: SponsorLevel;
  label: string;
}

const title = 'Patrocinadores';

const tierOrder: TierConfig[] = [
  { level: SponsorLevel.GOLD, label: 'Ouro' },
  { level: SponsorLevel.SILVER, label: 'Prata' },
  { level: SponsorLevel.BRONZE, label: 'Bronze' },
];

function getTierVisual(level: SponsorLevel) {
  if (level === SponsorLevel.GOLD) {
    return {
      minHeight: { xs: 98, md: 112 },
      maxLogoWidth: 200,
      logoHeight: 90,
      logoPadding: 0.95,
    };
  }

  if (level === SponsorLevel.SILVER) {
    return {
      minHeight: { xs: 88, md: 100 },
      maxLogoWidth: 172,
      logoHeight: 60,
      logoPadding: 0.8,
    };
  }

  return {
    minHeight: { xs: 80, md: 90 },
    maxLogoWidth: 148,
    logoHeight: 40,
    logoPadding: 0.65,
  };
}

export function Sponsors({ id }: SponsorsProps) {
  const { market } = useParams<YearbookParams>();

  return (
    <Box
      component="section"
      id={id}
      display="flex"
      flexDirection="column"
      gap={5}
      width="100%">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
        <Typography
          fontSize={{ xs: 32, md: 48 }}
          fontWeight={600}
          sx={{
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundImage: sectionTitleGradient,
          }}>
          {title}
        </Typography>
        <ShareButton market={market} section={kebabcase(title)} size={24} />
      </Box>

      {tierOrder.map((tier) => {
        const tierSponsors = sponsors.filter(
          (sponsor) => sponsor.level === tier.level,
        );
        const tierVisual = getTierVisual(tier.level);
        const isSingleSponsor = tierSponsors.length === 1;

        if (!tierSponsors.length) {
          return null;
        }

        return (
          <Box
            key={tier.level}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              fontSize={{ xs: 20, md: 30 }}
              fontWeight={700}
              textAlign="center"
              sx={{
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundImage: sectionTitleGradient,
              }}>
              Patrocinadores {tier.label}
            </Typography>

            <Box
              sx={{
                bgcolor: colors.bgSection,
                borderRadius: 3,
                border: '1px solid rgba(255,130,17,0.18)',
                p: { xs: 2, md: 3 },
                display: 'grid',
                gridTemplateColumns: isSingleSponsor
                  ? 'minmax(220px, 420px)'
                  : {
                      xs: 'repeat(1, minmax(0, 1fr))',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                      lg:
                        tier.level === SponsorLevel.GOLD
                          ? 'repeat(4, minmax(0, 1fr))'
                          : 'repeat(3, minmax(0, 1fr))',
                    },
                justifyContent: isSingleSponsor ? 'center' : 'stretch',
                gap: 1.5,
              }}>
              {tierSponsors.map((sponsor) => (
                <Box
                  key={sponsor.name}
                  component="a"
                  href={sponsor.site}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`acessar site ${sponsor.name}`}
                  sx={{
                    textDecoration: 'none',
                    minHeight: tierVisual.minHeight,
                    borderRadius: 2,
                    background:
                      'linear-gradient(120deg, rgba(255,130,17,0.1), rgba(255,130,17,0.03))',
                    border: '1px solid rgba(255,255,255,0.09)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1.5,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'rgba(255,130,17,0.8)',
                      transform: 'translateY(-2px)',
                      background:
                        'linear-gradient(120deg, rgba(255,130,17,0.16), rgba(255,130,17,0.05))',
                    },
                  }}>
                  <Box
                    component="img"
                    src={sponsor.imageUrl}
                    alt={sponsor.name}
                    sx={{
                      width: '100%',
                      maxWidth: tierVisual.maxLogoWidth,
                      height: tierVisual.logoHeight,
                      objectFit: 'contain',
                      bgcolor: 'white',
                      borderRadius: 1,
                      p: tierVisual.logoPadding,
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
